import React, { useContext, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../../Utils/LastActivity';
import { appBarHeight, navBarHeight } from '../../Constants';
import { sha256 } from 'shouldve-been-js';
import { useTitle } from 'shouldve-been-react';
import { PinCodeInput, SuccessMessage, ErrorMessage } from 'shouldve-been-mui';

type ChangePinSubTabProps = {
    closeSubTab: () => void,
};

function ChangePinSubTab(props: Readonly<ChangePinSubTabProps>): JSX.Element {

    useTitle('K-Fleet Experience - Change PIN');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const currentTheme: Theme = useTheme();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    async function handleComplete(pin: string): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const hashedPin: string | null = await sha256((userContext.currentUser?.username ?? '') + '_' + pin);

        if (hashedPin === null) {
            setErrorMessage('PIN could not be updated');
            return;
        }

        userContext.setHashedPin(hashedPin);
        localStorageContext.localStorageService.setHashedPin(hashedPin);

        setSuccessMessage('PIN updated');
    }

    return (
        <>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <IconButton 
                        edge='end' 
                        color='inherit' 
                        onClick={(): void => props.closeSubTab()}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                    <Typography style={{
                        marginLeft: '40px',
                        color: currentTheme.palette.text.primary,
                    }}>
                        Change PIN
                    </Typography>
                </Toolbar>
            </AppBar>
            <div style={{
                textAlign: 'center',
                padding: '55px 5px 5px 5px',
                height: windowSize[1] - navBarHeight - appBarHeight,
                overflow: 'auto',
            }}>
                <Typography style={{
                    color: currentTheme.palette.text.primary,
                }}>
                    Change your PIN code
                </Typography>
                <br />
                <PinCodeInput
                    length={4}
                    onComplete={handleComplete}
                />
                <SuccessMessage
                    message={successMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                />
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                />
            </div>
        </>
    );
}

export default ChangePinSubTab;